/**
 * Avatar API — cross-product user avatars via the shared user-profile service
 * (api.puglieseweb.com/profile). Uploads go straight to S3 via a presigned URL.
 */

import axios from 'axios';
import { PROFILE_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: PROFILE_API_URL });

export interface AvatarGetResponse {
  avatarUrl: string | null;
  exists: boolean;
  s3Key?: string;
}

export interface AvatarUploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface AvatarConfirmResponse {
  avatarUrl: string;
  s3Key: string;
  success: boolean;
}

export const avatarApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** Get a presigned URL for the current user's avatar (or exists=false). */
  async getAvatar(): Promise<AvatarGetResponse> {
    const { data } = await api.get<AvatarGetResponse>('/users/me/avatar');
    return data;
  },

  /** Get a presigned S3 URL for uploading a new avatar. */
  async getUploadUrl(contentType: string): Promise<AvatarUploadUrlResponse> {
    const { data } = await api.post<AvatarUploadUrlResponse>('/users/me/avatar/upload-url', {
      contentType,
    });
    return data;
  },

  /** PUT the image bytes directly to the presigned S3 URL (no auth header). */
  async uploadToS3(presignedUrl: string, file: Blob, contentType: string): Promise<void> {
    await axios.put(presignedUrl, file, { headers: { 'Content-Type': contentType } });
  },

  /** Confirm the upload landed and get a presigned view URL. */
  async confirmUpload(s3Key: string): Promise<AvatarConfirmResponse> {
    const { data } = await api.post<AvatarConfirmResponse>('/users/me/avatar/confirm', { s3Key });
    return data;
  },

  /** Full upload flow: presign → PUT → confirm. Returns the new view URL. */
  async upload(file: File): Promise<string> {
    const contentType = file.type || 'image/jpeg';
    const { uploadUrl, s3Key } = await this.getUploadUrl(contentType);
    await this.uploadToS3(uploadUrl, file, contentType);
    const { avatarUrl } = await this.confirmUpload(s3Key);
    return avatarUrl;
  },
};
